import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUI } from '../providers/UIProvider';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders } from '../lib/useQobuz';
import { useMaestroStore } from '../store/maestroStore';
import { QueueController } from '../transport/QueueController';
import { libraryService } from '../services/libraryService';
import { placeholderDataUri } from './library/AlbumGrid';
import { trackEvent } from '../hooks/usePersonalization';
import { getQualityTier, qualifiesHiResAudioBadge } from '../hooks/useAudioQuality';
import HiResAudioBadge from './HiResAudioBadge';
import useAlbumCredits from '../hooks/useAlbumCredits';
import useAppNavigation from '../hooks/useAppNavigation';
import TrackContextMenu from './TrackContextMenu';
import { useFavorites, usePlaylists, addToQueue } from '../lib/useUserData';

const GOLD = '#C9A84C';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getHiResCover(url) {
  if (!url) return url;
  const s = String(url);
  if (s.includes('static.qobuz.com')) return s.replace(/_\d+\.jpg/, '_org.jpg');
  return s;
}

function formatTrackDuration(val) {
  const s = typeof val === 'number' ? (val > 10000 ? Math.floor(val / 1000) : val) : parseInt(val);
  if (!s || isNaN(s) || s <= 0) return '';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function formatAlbumDuration(val) {
  if (!val) return '';
  const totalSeconds = val > 10000 ? Math.floor(val / 1000) : val;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return hours > 0 ? `${hours}hr ${minutes}min` : `${minutes}min`;
}

function cleanHtml(text) {
  if (!text) return '';
  let s = String(text);
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');
  s = s.replace(/<\/?[^>]+>/g, '');
  s = s.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  s = s.replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&nbsp;/g, ' ');
  s = s.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
  return s.trim();
}

const OVERLAY_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');

.fao-overlay { scrollbar-width: thin; scrollbar-color: rgba(201,168,76,0.3) transparent; }
.fao-overlay ::-webkit-scrollbar { width: 5px; }
.fao-overlay ::-webkit-scrollbar-track { background: transparent; }
.fao-overlay ::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.3); border-radius: 3px; }

.fao-right::-webkit-scrollbar { width: 5px; }
.fao-right::-webkit-scrollbar-track { background: transparent; }
.fao-right::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.25); border-radius: 3px; }

.fao-track:hover { background: rgba(201,168,76,0.06) !important; }
.fao-action:hover { background: rgba(201,168,76,0.12) !important; transform: scale(1.03); }
.fao-tab:hover { color: ${GOLD} !important; }
.fao-close:hover { background: var(--bg-elevated, rgba(255,255,255,0.85)) !important; transform: rotate(90deg); }
.fao-credit-link { cursor: pointer; transition: color 0.15s; }
.fao-credit-link:hover { color: ${GOLD} !important; }
.fao-genre-pill { transition: background 0.15s, color 0.15s, border-color 0.15s; }
.fao-genre-pill:hover { border-color: ${GOLD} !important; color: ${GOLD} !important; }

@keyframes faoCoverReveal { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
@keyframes faoSlideIn { from { opacity: 0; transform: translateX(24px); } to { opacity: 1; transform: translateX(0); } }
@keyframes faoShimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }

@media (max-width: 768px) {
  .fao-split { flex-direction: column !important; }
  .fao-left { position: relative !important; width: 100% !important; height: 50vh !important; min-height: 320px !important; max-height: 420px !important; }
  .fao-right { padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important; }
  .fao-overlay { overflow-y: auto !important; }
}
`;

export default function FullAlbumOverlay() {
  const { albumOverlay, setAlbumOverlay } = useUI();
  const nav = useAppNavigation();
  const globalTrack = useMaestroStore((s) => s.currentTrack);
  const globalPlaybackState = useMaestroStore((s) => s.playbackState);
  const { isFavorite: checkFav, toggleFavorite: toggleFav } = useFavorites();
  const { playlists, createPlaylist: createPl, addTrackToPlaylist: addToPl } = usePlaylists();
  const [displayAlbum, setDisplayAlbum] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState('tracks');
  const [playingTrackIdx, setPlayingTrackIdx] = useState(null);
  const [qobuzDetail, setQobuzDetail] = useState(null);
  const [displayTracks, setDisplayTracks] = useState([]);
  const [artistBio, setArtistBio] = useState('');
  const [albumMeta, setAlbumMeta] = useState(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [hiResCover, setHiResCover] = useState('');
  const [qobuzAlbum, setQobuzAlbum] = useState(null);
  const [trackMenu, setTrackMenu] = useState(null);
  const rightRef = useRef(null);
  const isPlaying = globalPlaybackState === 'playing';
  

  const album = displayAlbum;
  const isLocalAlbum = !!(album?.source === 'local_library' || album?.__source === 'local_library' || album?.isLocalLibrary);

  const qobuzAlbumId = useMemo(() => {
    if (!album) return null;
    const sid = album.serviceAlbumId || (album.source === 'qobuz' ? album.id : null);
    if (sid && !UUID_RE.test(String(sid))) return String(sid);
    if (album.id && !UUID_RE.test(String(album.id))) return String(album.id);
    return null;
  }, [album?.id, album?.serviceAlbumId, album?.source]);

  const { creditsByRole, loading: creditsLoading } = useAlbumCredits({
    albumId: album?.id || null,
    albumTitle: album?.title || null,
    artistName: album?.artistName || album?.artist || null,
    qobuzAlbumId: qobuzAlbumId || null,
    qobuzFullAlbum: qobuzDetail || null,
    releaseGroupMbid: album?.musicbrainz_release_id || album?.mbid || null,
    apiAlbum: null,
    album: album || null,
  });

  // Sync active track index with global playback state
  const activeTrackIdx = useMemo(() => {
    if (!globalTrack || !displayTracks.length) return -1;
    const gid = String(globalTrack.qobuzTrackId || globalTrack.id || '');
    for (let i = 0; i < displayTracks.length; i++) {
      const t = displayTracks[i];
      const tid = String(t.qobuzTrackId || t.serviceTrackId || t.id || '');
      if (tid && tid === gid) return i;
      if (t.title && globalTrack.title && t.title === globalTrack.title &&
          (t.artistName || t.artist || '') === (globalTrack.artist || '')) return i;
    }
    return -1;
  }, [globalTrack?.id, globalTrack?.qobuzTrackId, globalTrack?.title, displayTracks]);

  // Build genre pills from album + Qobuz data
  const genrePills = useMemo(() => {
    if (!album) return [];
    const pills = [];
    const seen = new Set();
    const addGenre = (g) => {
      if (!g) return;
      const obj = typeof g === 'object' ? g : { name: String(g), id: null };
      const name = (obj.name || obj.tag || '').trim();
      if (!name || seen.has(name.toLowerCase())) return;
      seen.add(name.toLowerCase());
      pills.push({ name, id: obj.id || null, slug: obj.slug || obj.path || null });
    };
    if (album.genre) addGenre(album.genre);
    if (album.subgenre) addGenre(album.subgenre);
    if (album.genres) album.genres.forEach(addGenre);
    if (album.genres_list) album.genres_list.forEach(addGenre);
    if (qobuzDetail?.genre) addGenre(qobuzDetail.genre);
    if (qobuzDetail?.genres_list) qobuzDetail.genres_list.forEach(addGenre);
    if (qobuzAlbum?.genre) addGenre(qobuzAlbum.genre);
    if (qobuzAlbum?.genres) qobuzAlbum.genres.forEach(addGenre);
    return pills.slice(0, 5);
  }, [album?.genre, album?.subgenre, album?.genres, album?.genres_list, qobuzDetail, qobuzAlbum]);

  const totalDuration = useMemo(() => {
    if (!album) return '';
    if (displayTracks.length) {
      const total = displayTracks.reduce((sum, t) => {
        const d = t.duration_ms || t.duration || 0;
        return sum + (d > 10000 ? d : d * 1000);
      }, 0);
      return formatAlbumDuration(total);
    }
    return formatAlbumDuration(album?.duration || album?.total_duration_ms || 0);
  }, [displayTracks, album?.duration, album?.total_duration_ms]);

  // ── Album open ─────────────────────────────────────────────
  useEffect(() => {
    if (!albumOverlay) return;
    if (displayAlbum && String(displayAlbum.id) === String(albumOverlay.id)) return;
    setDisplayAlbum(albumOverlay);
    setIsOpen(true);
    setActiveTab('tracks');
    setQobuzDetail(null);
    setQobuzAlbum(null);
    setAlbumMeta(null);
    setDisplayTracks(albumOverlay.tracks?.length ? albumOverlay.tracks : []);
    setArtistBio('');
    setPlayingTrackIdx(null);
    setBioExpanded(false);
    setIsFavorite(false);
    const raw = albumOverlay.coverUrl || albumOverlay.cover || albumOverlay.imageUrl || albumOverlay.image?.large || '';
    setHiResCover(getHiResCover(raw));
    if (rightRef.current) rightRef.current.scrollTop = 0;
    requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
  }, [albumOverlay]);

  const close = useCallback(() => {
    setMounted(false);
    setTrackMenu(null);
    setTimeout(() => {
      setIsOpen(false);
      setDisplayAlbum(null);
      setAlbumOverlay(null);
    }, 400);
  }, [setAlbumOverlay]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // ── Data fetching (Qobuz + album meta, or local library) ───
  useEffect(() => {
    if (!album) return;
    const controller = new AbortController();
    const signal = controller.signal;

    const isLocal = album.source === 'local_library' || album.__source === 'local_library' || album.isLocalLibrary;

    if (isLocal && album.album_key) {
      (async () => {
        try {
          const resp = await libraryService.getAlbum(album.album_key);
          if (signal.aborted) return;
          if (resp?.success && resp.tracks?.length) {
            const tracks = resp.tracks.map((t, i) => ({
              id: t.id || `local_${i}`,
              title: t.title || 'Unknown',
              artistName: t.artist || album.artistName || album.artist || 'Unknown',
              artist: t.artist || album.artistName || album.artist || 'Unknown',
              duration: Number(t.duration) || 0,
              duration_ms: (Number(t.duration) || 0) * 1000,
              trackNumber: t.track_num || i + 1,
              discNumber: t.disc_num || 1,
              source: 'local_library',
              file_path: t.file_path,
              machine_id: t.machine_id,
              format: t.format,
              maximum_bit_depth: t.bit_depth,
              maximum_sampling_rate: t.sample_rate ? t.sample_rate / 1000 : null,
            }));
            setDisplayTracks(tracks);
          }
          if (resp?.album?.artist_bio && !signal.aborted) {
            setArtistBio(resp.album.artist_bio);
          }
          if (resp?.album?.cover_art_url && !signal.aborted) {
            setHiResCover(resp.album.cover_art_url);
          }
        } catch (e) {
          if (e.name !== 'AbortError') console.debug('[FullAlbumOverlay] Local library fetch failed:', e);
        }
      })();
      trackEvent('album_viewed', {
        entityType: 'album',
        entityId: album.album_key || album.id,
        entityName: album.title,
        metadata: { artistName: album.artistName || album.artist, source: 'local_library' },
      });
      return () => controller.abort();
    }

    const fetchQobuzDetail = async () => {
      if (!qobuzAlbumId) return;
      try {
        const res = await fetch(
          `${CLOUD_API_URL}/api/streaming/qobuz/album-detail?album_id=${qobuzAlbumId}`,
          { headers: getHeaders(), signal }
        );
        if (res.ok) {
          const data = await res.json();
          if (!signal.aborted) setQobuzDetail(data);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.debug('[FullAlbumOverlay] Detail fetch failed:', e);
      }
    };

    const fetchQobuzAlbum = async () => {
      if (!qobuzAlbumId) return;
      try {
        const res = await fetch(
          `${CLOUD_API_URL}/api/streaming/qobuz/album?albumId=${qobuzAlbumId}`,
          { headers: getHeaders(), signal }
        );
        if (res.ok) {
          const data = await res.json();
          if (!signal.aborted) {
            setQobuzAlbum(data);
            if (!album.tracks?.length && data?.tracks?.length) {
              setDisplayTracks(data.tracks);
            }
            const large = data?.image?.large || data?.coverUrl || '';
            if (large) setHiResCover(getHiResCover(large));
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.debug('[FullAlbumOverlay] Qobuz album fetch failed:', e);
      }
    };

    const fetchTracksFallback = async () => {
      if (album.tracks?.length || qobuzAlbumId) return;
      if (!album.id) return;
      try {
        const res = await fetch(
          `${CLOUD_API_URL}/api/albums/${encodeURIComponent(album.id)}`,
          { signal }
        );
        if (res.ok) {
          const data = await res.json();
          if (!signal.aborted && data?.tracks?.length) setDisplayTracks(data.tracks);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.debug('[FullAlbumOverlay] Track fallback failed:', e);
      }
    };

    const fetchAlbumMeta = async () => {
      const albumId = album.id || album.albumId;
      if (!albumId) return;
      try {
        const res = await fetch(
          `${CLOUD_API_URL}/api/albums/${encodeURIComponent(albumId)}?_cb=${Date.now()}`,
          { signal }
        );
        if (res.ok) {
          const data = await res.json();
          if (!signal.aborted) {
            setAlbumMeta(data);
            if (!album.tracks?.length && !qobuzAlbumId && data?.tracks?.length) {
              setDisplayTracks(data.tracks);
            }
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.debug('[FullAlbumOverlay] Album meta fetch failed:', e);
      }
    };

    fetchQobuzDetail();
    fetchQobuzAlbum();
    fetchAlbumMeta();
    fetchTracksFallback();
    trackEvent('album_viewed', {
      entityType: 'album',
      entityId: album.id,
      entityName: album.title,
      metadata: { artistName: album.artistName || album.artist },
    });
    return () => controller.abort();
  }, [album?.id, album?.album_key, qobuzAlbumId]);

  // ── Artist bio fetch (own controller so it never gets aborted by Qobuz re-fetches) ──
  useEffect(() => {
    if (!album) return;
    const lookup = album.artistId || album.artist_id || album.artistName || album.artist_name || album.artist;
    if (!lookup) return;
    const controller = new AbortController();
    const signal = controller.signal;
    (async () => {
      try {
        const res = await fetch(
          `${CLOUD_API_URL}/api/artists/${encodeURIComponent(lookup)}?_cb=${Date.now()}`,
          { signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (signal.aborted) return;
        const bio = data?.biographyNarrative || data?.biography_narrative ||
          data?.biography || data?.bioSummary || data?.bio_summary || '';
        if (bio) setArtistBio(bio);
      } catch (e) {
        if (e.name !== 'AbortError') console.debug('[FullAlbumOverlay] Artist bio fetch failed:', e);
      }
    })();
    return () => controller.abort();
  }, [album?.id]);

  // ── Playback handlers ──────────────────────────────────────
  const handleTrackPlay = useCallback((trackIndex) => {
    if (!album || !displayTracks.length) return;

    const coverUrl = album.coverUrl || album.cover || album.imageUrl || '';
    const albumArtist = album.artistName || album.artist || '';

    const formattedTracks = displayTracks.map((t, i) => {
      const bd = t.maximum_bit_depth || t.maxBitDepth || album.maxBitDepth || album.maximum_bit_depth;
      const sr = t.maximum_sampling_rate || t.maxSampleRate || album.maxSampleRate || album.maximum_sampling_rate;
      const tier = getQualityTier(bd, sr);
      return {
        id: t.id || t.trackId || `track_${i}`,
        title: t.title || 'Unknown',
        artist: t.artistName || t.artist || albumArtist,
        album: album.title || '',
        albumId: album.id || null,
        albumTitle: album.title || '',
        artworkUrl: t.artworkUrl || t.coverUrl || coverUrl,
        duration: t.duration_ms ? Math.floor(t.duration_ms / 1000) : (t.duration || 0),
        source: t.source || album.source || 'maestro',
        qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || t.id,
        file_path: t.file_path,
        machine_id: t.machine_id,
        bitDepth: bd,
        sampleRate: sr,
        format: tier && tier.tier !== 'cd' ? `${tier.label} FLAC` : (t.format || album.audioFormat || 'FLAC'),
      };
    });

    QueueController.playFrom(formattedTracks, trackIndex);
    setPlayingTrackIdx(trackIndex);
  }, [album, displayTracks]);

  const handlePlayAll = useCallback(() => {
    if (displayTracks.length) handleTrackPlay(0);
  }, [handleTrackPlay, displayTracks]);

  const buildQueueItems = useCallback(() => {
    if (!displayTracks.length || !album) return [];
    const coverUrl = album.coverUrl || album.cover || album.imageUrl || '';
    return displayTracks.map((t, i) => ({
      track: {
        id: t.id || t.trackId || `track_${i}`,
        title: t.title || 'Unknown',
        artist: t.artistName || t.artist || album.artistName || album.artist || '',
        album: album.title || '',
        artworkUrl: t.artworkUrl || t.coverUrl || coverUrl,
        duration: t.duration_ms ? Math.floor(t.duration_ms / 1000) : (t.duration || 0),
        source: t.source || album.source || 'maestro',
        file_path: t.file_path,
        machine_id: t.machine_id,
      },
    }));
  }, [album, displayTracks]);

  const handlePlayNext = useCallback(() => {
    const items = buildQueueItems();
    if (items.length) useMaestroStore.getState().playNext(items);
  }, [buildQueueItems]);

  const handleAddToQueue = useCallback(() => {
    const items = buildQueueItems();
    if (items.length) useMaestroStore.getState().addToQueue(items);
  }, [buildQueueItems]);

  const handleTrackMenu = useCallback((e, track, trackIndex) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setTrackMenu({
      track,
      index: trackIndex,
      position: {
        x: Math.min(rect.right, window.innerWidth - 290),
        y: rect.top,
      },
    });
  }, []);

  const handleCreatePlaylistAndAdd = useCallback(async (name, track) => {
    const pl = await createPl(name);
    const cover = album?.coverUrl || album?.cover || album?.imageUrl || '';
    if (pl?.id && track) await addToPl(pl.id, { ...track, coverUrl: cover });
  }, [createPl, addToPl, album]);

  // ── Derived: which track is active ─────────────────────────
  const resolvedActiveIdx = activeTrackIdx >= 0 ? activeTrackIdx : playingTrackIdx;

  if (!isOpen || !album) return null;

  // ── Resolved display values ────────────────────────────────
  const rawCoverUrl = hiResCover || getHiResCover(
    qobuzAlbum?.image?.large || qobuzAlbum?.coverUrl ||
    album.coverUrl || album.cover || album.imageUrl || album.image?.large || ''
  );
  const title = album.title || 'Untitled';
  const coverUrl = rawCoverUrl || (isLocalAlbum ? placeholderDataUri(title) : '');
  const artist = album.artistName || album.artist || 'Unknown Artist';
  const year = album.releaseYear || album.year || '';
  const label = typeof album.label === 'object' ? album.label?.name : (album.label || '');
  const trackCount = displayTracks.length || album.trackCount || album.tracksCount || 0;
  const bitDepth = album.maxBitDepth || album.maximum_bit_depth;
  const sampleRate = album.maxSampleRate || album.maximum_sampling_rate;
  const tier = getQualityTier(bitDepth, sampleRate);
  const qualityDetail = bitDepth && sampleRate ? `${bitDepth}-bit / ${sampleRate}kHz` : '';
  const audioFormat = album.audioFormat || album.format || 'FLAC';

  const description = cleanHtml(
    qobuzDetail?.description || qobuzAlbum?.description || album.description ||
    albumMeta?.description || albumMeta?.biography_narrative || albumMeta?.annotation || ''
  );
  const linerNotes = cleanHtml(
    qobuzDetail?.editorial || qobuzDetail?.catchline || qobuzAlbum?.editorial ||
    album.biographyNarrative || albumMeta?.editorial || albumMeta?.liner_notes || ''
  );

  return (
    <>
      <style>{OVERLAY_STYLES}</style>
      <div
        className="fao-overlay"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          bottom: 'var(--mini-player-height, 72px)',
          zIndex: 49,
          background: 'var(--bg-primary, #ffffff)',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.4s ease',
          color: 'var(--text-primary, #1d1d1f)',
          overflow: 'hidden',
        }}
      >
        {/* Close button */}
        <button
          onClick={close}
          className="fao-close"
          style={{
            position: 'absolute', top: 'max(20px, env(safe-area-inset-top, 20px))', right: 24, zIndex: 100,
            background: 'var(--bg-secondary, #f5f5f7)',
            border: '1px solid var(--border-light, #e8e8ed)',
            color: 'var(--text-secondary, #6e6e73)',
            width: 36, height: 36, borderRadius: '50%',
            fontSize: 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.25s ease',
            backdropFilter: 'blur(10px)',
          }}
        >✕</button>

        {/* Two-column split layout */}
        <div className="fao-split" style={{ display: 'flex', height: '100%' }}>

          {/* ═══ LEFT: Cover art ═══ */}
          <div
            className="fao-left"
            style={{
              width: '42%', flexShrink: 0, position: 'relative',
              overflow: 'hidden',
              animation: mounted ? 'faoCoverReveal 0.6s ease forwards' : 'none',
            }}
          >
            {coverUrl ? (
              <>
                <img
                  src={coverUrl}
                  alt={title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 120,
                  background: 'linear-gradient(to top, var(--bg-primary, #fff), transparent)',
                  pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute', top: 0, bottom: 0, right: 0, width: 80,
                  background: 'linear-gradient(to left, var(--bg-primary, #fff), transparent)',
                  pointerEvents: 'none',
                }} />
              </>
            ) : (
              <div style={{
                width: '100%', height: '100%',
                background: 'var(--bg-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 80, color: 'var(--text-tertiary, #86868b)',
              }}>♪</div>
            )}
          </div>

          {/* ═══ RIGHT: All content ═══ */}
          <div
            className="fao-right"
            ref={rightRef}
            style={{
              flex: 1, height: '100%', overflowY: 'auto',
              padding: '48px 36px 96px 36px',
              animation: mounted ? 'faoSlideIn 0.5s 0.15s ease both' : 'none',
            }}
          >
            {/* Genre pills */}
            {genrePills.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {genrePills.map((g, i) => (
                  <button
                    key={i}
                    className="fao-genre-pill"
                    onClick={(e) => {
                      e.stopPropagation();
                      close();
                      setTimeout(() => nav.openGenre(g.slug || g.name), 450);
                    }}
                    style={{
                      padding: '5px 14px', borderRadius: 999,
                      background: 'var(--bg-secondary, #f5f5f7)',
                      border: '1px solid var(--border, #d2d2d7)',
                      color: 'var(--text-secondary, #6e6e73)',
                      fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                      textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >{g.name}</button>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontWeight: 300, fontSize: 'clamp(32px, 4vw, 50px)', lineHeight: 1.1,
              marginBottom: 12, letterSpacing: '-0.5px',
              color: 'var(--text-primary, #1d1d1f)',
            }}>{title}</h1>

            {/* Artist */}
            <p style={{
              fontFamily: 'Montserrat, sans-serif', color: GOLD,
              fontSize: 15, letterSpacing: 1.5, marginBottom: 24, fontWeight: 500,
            }}>{artist}</p>

            {/* Quality + format + source badges */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
              {isLocalAlbum && (
                <span style={{
                  background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#f59e0b',
                  fontFamily: 'Montserrat, sans-serif', fontSize: 10,
                  letterSpacing: 2, padding: '4px 12px', borderRadius: 3, fontWeight: 800,
                }}>LOCAL LIBRARY</span>
              )}
              {album?.is_compilation && (
                <span style={{
                  background: 'rgba(168, 85, 247, 0.15)', border: '1px solid rgba(168, 85, 247, 0.3)', color: '#a855f7',
                  fontFamily: 'Montserrat, sans-serif', fontSize: 10,
                  letterSpacing: 2, padding: '4px 12px', borderRadius: 3, fontWeight: 800,
                }}>COMPILATION</span>
              )}
              {tier && (
                qualifiesHiResAudioBadge(bitDepth, sampleRate) ? (
                  <HiResAudioBadge bitDepth={bitDepth} sampleRate={sampleRate} height={20} />
                ) : (
                  <span style={{
                    background: tier.bg, border: tier.border, color: tier.color,
                    fontFamily: 'Montserrat, sans-serif', fontSize: 10,
                    letterSpacing: 2, padding: '4px 12px', borderRadius: 3,
                    backgroundSize: '200% auto',
                    animation: tier.tier === 'hi-res' ? 'faoShimmer 3s linear infinite' : 'none',
                  }}>{tier.label}</span>
                )
              )}
              {qualityDetail && (
                <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: 'var(--text-secondary, #6e6e73)' }}>{qualityDetail}</span>
              )}
              {audioFormat && (
                <>
                  <span style={{ color: 'var(--text-tertiary, #86868b)' }}>·</span>
                  <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: 'var(--text-secondary, #6e6e73)' }}>{audioFormat}</span>
                </>
              )}
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 28, marginBottom: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'TRACKS', value: trackCount || '—' },
                { label: 'DURATION', value: totalDuration || '—' },
                { label: 'YEAR', value: year || '—' },
                ...(label ? [{ label: 'LABEL', value: label }] : []),
              ].map(s => (
                <div key={s.label}>
                  <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, letterSpacing: 2, color: 'var(--text-tertiary, #86868b)', marginBottom: 3 }}>{s.label}</p>
                  <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, color: 'var(--text-primary, #1d1d1f)', fontWeight: 500 }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
              <button onClick={handlePlayAll} style={{
                background: GOLD, color: '#000', border: 'none',
                padding: '12px 28px', fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700, fontSize: 11, letterSpacing: 2,
                borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s',
              }}>▶ PLAY ALL</button>
              {['PLAY NEXT', '+ QUEUE', isFavorite ? '♥' : '♡'].map((btn, i) => (
                <button key={btn} className="fao-action" onClick={() => {
                  if (i === 0) handlePlayNext();
                  else if (i === 1) handleAddToQueue();
                  else setIsFavorite(f => !f);
                }} style={{
                  background: 'var(--bg-secondary, #f5f5f7)',
                  border: '1px solid var(--border-light, #e8e8ed)',
                  color: (i === 2 && isFavorite) ? GOLD : 'var(--text-secondary, #6e6e73)',
                  padding: '12px 16px', fontFamily: 'Montserrat, sans-serif', fontSize: 11,
                  letterSpacing: btn.length > 2 ? 1 : 0, borderRadius: 4,
                  cursor: 'pointer', transition: 'all 0.2s',
                }}>{btn}</button>
              ))}
            </div>

            {/* Description */}
            {description && (
              <div style={{ marginBottom: 24 }}>
                <p style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic',
                  fontSize: 16, lineHeight: 1.8, color: 'var(--text-secondary, #6e6e73)',
                  fontWeight: 300,
                }}>
                  "{description.length > 400 ? description.slice(0, 400).trim() + '…' : description}"
                </p>
              </div>
            )}

            {/* Gold divider */}
            <div style={{ height: 1, background: 'linear-gradient(to right, rgba(201,168,76,0.4), transparent)', marginBottom: 24 }} />

            {/* Liner notes */}
            {linerNotes && (
              <div style={{ marginBottom: 24 }}>
                <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, letterSpacing: 3, color: GOLD, marginBottom: 12 }}>LINER NOTES</p>
                <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 16, lineHeight: 1.8, color: 'var(--text-secondary, #6e6e73)', fontWeight: 300 }}>{linerNotes}</p>
              </div>
            )}

            {/* Artist bio — expand/collapse */}
            {artistBio && (
              <div
                style={{ marginBottom: 24, cursor: 'pointer' }}
                onClick={() => setBioExpanded(e => !e)}
              >
                <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, letterSpacing: 3, color: GOLD, marginBottom: 12 }}>
                  ABOUT THE ARTIST {bioExpanded ? '▾' : '▸'}
                </p>
                <p style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: 16, lineHeight: 1.8,
                  color: 'var(--text-secondary, #6e6e73)', fontWeight: 300,
                  overflow: 'hidden',
                }}>
                  {bioExpanded
                    ? artistBio
                    : (artistBio.length > 200 ? artistBio.slice(0, 200).trim() + '…' : artistBio)
                  }
                </p>
                {artistBio.length > 200 && (
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif', fontSize: 12,
                    color: GOLD, fontWeight: 500, marginTop: 4, display: 'inline-block',
                  }}>
                    {bioExpanded ? 'Show less' : 'Read more'}
                  </span>
                )}
              </div>
            )}

            {/* Tabs */}
            <div style={{
              display: 'flex', gap: 0,
              borderBottom: '1px solid var(--border-light, #e8e8ed)',
              marginBottom: 8, position: 'sticky', top: 0,
              background: 'var(--bg-primary, #fff)', zIndex: 5,
              paddingTop: 4,
            }}>
              {['tracks', 'credits'].map(tab => (
                <button key={tab} className="fao-tab" onClick={() => setActiveTab(tab)} style={{
                  background: 'transparent', border: 'none',
                  borderBottom: activeTab === tab ? `2px solid ${GOLD}` : '2px solid transparent',
                  color: activeTab === tab ? GOLD : 'var(--text-tertiary, #86868b)',
                  fontFamily: 'Montserrat, sans-serif', fontSize: 10, letterSpacing: 3,
                  padding: '10px 20px', cursor: 'pointer', textTransform: 'uppercase',
                  marginBottom: -1, transition: 'color 0.2s',
                }}>{tab}</button>
              ))}
            </div>

            {/* ── Tracks tab ── */}
            {activeTab === 'tracks' && (
              <div>
                {displayTracks.length === 0 && (
                  <div style={{ padding: '40px 0', textAlign: 'center', fontFamily: 'Montserrat, sans-serif', fontSize: 13, color: 'var(--text-tertiary)' }}>
                    Loading tracks…
                  </div>
                )}
                {displayTracks.map((track, i) => {
                  const trackNum = track.track_number || track.position || track.trackNumber || (i + 1);
                  const trackTitle = track.title || 'Untitled';
                  const trackArtist = track.artistName || track.artist_name || track.artist || '';
                  const dur = formatTrackDuration(track.duration_ms || track.duration || 0);
                  const bd = track.maximum_bit_depth || track.maxBitDepth || bitDepth;
                  const sr = track.maximum_sampling_rate || track.maxSampleRate || sampleRate;
                  const trackTier = getQualityTier(bd, sr);
                  const isActive = resolvedActiveIdx === i;
                  return (
                    <div
                      key={track.id || i}
                      className="fao-track"
                      onClick={() => handleTrackPlay(i)}
                      style={{
                        display: 'flex', alignItems: 'center',
                        minHeight: 52, padding: '6px 8px',
                        background: isActive ? 'rgba(201,168,76,0.08)' : i % 2 === 0 ? 'var(--bg-primary, transparent)' : 'var(--bg-secondary, rgba(0,0,0,0.02))',
                        borderLeft: isActive ? `3px solid ${GOLD}` : '3px solid transparent',
                        cursor: 'pointer', transition: 'background 0.15s',
                        borderBottom: '1px solid var(--border-light, rgba(0,0,0,0.05))',
                      }}
                    >
                      {/* VU meter (active+playing) or play icon + track number */}
                      <div style={{ width: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        {isActive ? (
                          <div className={`vu-meter${!isPlaying ? ' paused' : ''}`}>
                            <span /><span /><span />
                          </div>
                        ) : (
                          <>
                            <svg style={{ width: 7, height: 9, color: 'var(--text-secondary, rgba(0,0,0,0.25))' }} viewBox="0 0 7 9" fill="currentColor"><path d="M0 0v9l7-4.5z"/></svg>
                            <span style={{ fontSize: 13, color: 'var(--text-secondary, rgba(0,0,0,0.4))', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{trackNum}</span>
                          </>
                        )}
                      </div>

                      {/* Title + artist */}
                      <div style={{ flex: 1, minWidth: 0, paddingLeft: 8, paddingRight: 8 }}>
                        <p style={{
                          fontSize: 14, fontWeight: 600, margin: 0,
                          color: isActive ? GOLD : 'var(--text-primary, #1d1d1f)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{trackTitle}</p>
                        {trackArtist && (
                          <p style={{
                            fontSize: 12, margin: '2px 0 0', color: 'var(--text-secondary, #6e6e73)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>{trackArtist}</p>
                        )}
                      </div>

                      {/* Quality badge */}
                      {trackTier && (
                        <span style={{
                          background: trackTier.bg, color: trackTier.color, border: trackTier.border,
                          fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 3,
                          flexShrink: 0, marginRight: 8,
                        }}>{trackTier.label}</span>
                      )}

                      {/* Duration */}
                      {dur && (
                        <span style={{
                          fontSize: 13, color: 'var(--text-secondary, rgba(0,0,0,0.4))',
                          minWidth: 40, textAlign: 'right', flexShrink: 0,
                          fontVariantNumeric: 'tabular-nums',
                        }}>{dur}</span>
                      )}

                      {/* Heart */}
                      <button
                        onClick={(e) => { e.stopPropagation(); }}
                        style={{
                          background: 'none', border: 'none', padding: '4px 6px',
                          color: 'var(--text-secondary, rgba(0,0,0,0.3))',
                          cursor: 'pointer', flexShrink: 0, marginLeft: 4,
                        }}
                      >
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </button>

                      {/* Three-dot menu */}
                      <button
                        onClick={(e) => handleTrackMenu(e, track, i)}
                        style={{
                          background: 'none', border: 'none', padding: '4px 4px',
                          color: 'var(--text-secondary, rgba(0,0,0,0.3))',
                          cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Credits tab ── */}
            {activeTab === 'credits' && (
              <div style={{ paddingTop: 8 }}>
                {creditsLoading && (
                  <div style={{ padding: '40px 0', textAlign: 'center', fontFamily: 'Montserrat, sans-serif', fontSize: 13, color: 'var(--text-tertiary)' }}>
                    Loading credits…
                  </div>
                )}
                {!creditsLoading && !creditsByRole && (
                  <div style={{ padding: '40px 0', textAlign: 'center', fontFamily: 'Montserrat, sans-serif', fontSize: 13, color: 'var(--text-tertiary)' }}>
                    No credits available
                  </div>
                )}
                {creditsByRole && Object.entries(creditsByRole).map(([role, people]) => (
                  people.map((person, j) => (
                    <div key={`${role}-${j}`} style={{
                      display: 'flex', gap: 20, padding: '12px 8px',
                      borderBottom: '1px solid var(--border-light, rgba(0,0,0,0.06))',
                    }}>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif', fontSize: 9, letterSpacing: 2,
                        color: 'var(--text-tertiary, #86868b)',
                        minWidth: 90, paddingTop: 3, flexShrink: 0,
                      }}>{role.toUpperCase()}</span>
                      <span
                        className="fao-credit-link"
                        onClick={() => {
                          const pid = person.id || person.artist_id || person.mbid || null;
                          const name = person.name || '';
                          close();
                          setTimeout(() => {
                            if (pid) nav.openPerson(pid, name, role);
                            else if (name) nav.openArtist({ name, __source: isLocalAlbum ? 'local_library' : undefined });
                          }, 450);
                        }}
                        style={{
                          fontFamily: "'Cormorant Garamond', Georgia, serif",
                          fontSize: 16, color: 'var(--text-primary, #1d1d1f)',
                          cursor: 'pointer',
                        }}
                      >
                        {person.name}
                        {person.instruments && (
                          <span style={{ color: 'var(--text-tertiary)', fontSize: 13, marginLeft: 8 }}>
                            ({person.instruments})
                          </span>
                        )}
                      </span>
                    </div>
                  ))
                ))}

                {/* Bonus content (goodies) */}
                {qobuzDetail?.goodies?.length > 0 && (
                  <div style={{ marginTop: 28 }}>
                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, letterSpacing: 3, color: GOLD, marginBottom: 12 }}>BONUS CONTENT</p>
                    {qobuzDetail.goodies.map((g, i) => (
                      <div key={i} style={{
                        padding: '10px 8px',
                        borderBottom: '1px solid var(--border-light)',
                        fontFamily: 'Montserrat, sans-serif', fontSize: 13,
                        color: 'var(--text-secondary)',
                      }}>
                        {g.name || g.description || 'Bonus content'}
                        {g.file_format_id && (
                          <span style={{ color: 'var(--text-tertiary)', marginLeft: 8 }}>
                            ({g.file_format_id === 21 ? 'PDF Booklet' : 'Digital Booklet'})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Track context menu */}
      {trackMenu && (
        <TrackContextMenu
          track={trackMenu.track}
          album={album}
          position={trackMenu.position}
          onClose={() => setTrackMenu(null)}
          onPlayNow={(t) => {
            const idx = displayTracks.findIndex(dt => (dt.id || dt.serviceTrackId) === (t.id || t.serviceTrackId));
            handleTrackPlay(idx >= 0 ? idx : 0);
          }}
          onPlayNext={(t) => {
            const covUrl = album?.coverUrl || album?.cover || album?.imageUrl || '';
            const artistName = t.artistName || t.artist || album?.artistName || album?.artist || '';
            useMaestroStore.getState().playNext([{ track: { id: t.id || t.serviceTrackId, title: t.title || 'Unknown', artist: artistName, album: album?.title || '', artworkUrl: covUrl, duration: t.duration_ms ? Math.floor(t.duration_ms / 1000) : (t.duration || 0), streamUrl: '', source: t.source || album?.source || 'maestro', qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || t.id } }]);
          }}
          onAddToQueue={(t) => addToQueue({ ...t, albumTitle: album?.title || '', coverUrl: album?.coverUrl || album?.cover || '', source: t.source || album?.source })}
          playlists={playlists}
          onAddToPlaylist={(plId, t) => addToPl(plId, { ...t, coverUrl: album?.coverUrl || album?.cover || '' })}
          onCreatePlaylist={handleCreatePlaylistAndAdd}
          onGoToArtist={(name) => nav.openArtist({ name, __source: isLocalAlbum ? 'local_library' : undefined })}
          onGoToAlbum={() => { if (album) nav.openAlbum(album); }}
          isFav={trackMenu.track ? checkFav('track', String(trackMenu.track.id || trackMenu.track.serviceTrackId || trackMenu.index)) : false}
          onToggleFavorite={() => {
            const t = trackMenu.track;
            const trackId = String(t?.id || t?.serviceTrackId || trackMenu.index);
            toggleFav('track', trackId, t?.source || album?.source || 'maestro', {
              title: t?.title, artistName: t?.artistName || t?.artist || album?.artistName || '', duration: t?.duration_ms,
              coverUrl: album?.coverUrl || album?.cover || '',
              albumId: String(album?.serviceAlbumId || album?.id || ''), albumTitle: album?.title || '',
            });
          }}
        />
      )}
    </>
  );
}
